import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MapPin, Gift, Calendar, Sparkles } from 'lucide-react';

/**
 * Romantic Template - Estilo suave e romântico
 * Paleta: tons pastel (rosa, lavanda, pêssego)
 * Tipografia: delicada
 * Animações: mais suaves (fade + scale)
 */

const fadeScale = {
  initial: { opacity: 0, scale: 0.95 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.8, ease: "easeOut" }
};

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.9, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.15 }
  },
  viewport: { once: true }
};

const floatAnimation = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

export function RomanticTemplate({ siteData, presentes, onPresentear, formatCurrency }) {
  const {
    couple_names = '',
    wedding_date = '',
    wedding_location = '',
    story = '',
    message = '',
    cover_image_url = '',
    gallery_images = []
  } = siteData || {};

  const formattedDate = wedding_date ? new Date(wedding_date).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }) : '';

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50" 
      style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" }}
    >
      {/* Decorative elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-0">
        <motion.div 
          className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-rose-200/30 to-pink-200/30 rounded-full blur-3xl"
          animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-tr from-lavender-200/20 to-rose-200/20 rounded-full blur-3xl"
          animate={{ scale: [1, 1.15, 1], rotate: [0, -5, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        {cover_image_url && (
          <>
            <div className="absolute inset-0">
              <img 
                src={cover_image_url} 
                alt="Capa do casamento"
                className="w-full h-full object-cover object-center"
                style={{ objectPosition: 'center 30%' }}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-rose-50/50 to-white/80" />
          </>
        )}
        
        {/* Content */}
        <motion.div 
          className="relative z-10 text-center px-6 max-w-4xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          {/* Decorative Hearts */}
          <motion.div className="mb-8" {...floatAnimation}>
            <div className="flex items-center justify-center gap-4">
              <Sparkles className="text-rose-300" size={24} />
              <Heart className="text-rose-400 fill-rose-200" size={40} />
              <Sparkles className="text-rose-300" size={24} />
            </div>
          </motion.div>
          
          <motion.p 
            className="text-rose-400 tracking-[0.4em] uppercase text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Celebrando o amor
          </motion.p>
          
          <motion.h1 
            className="text-5xl md:text-6xl lg:text-8xl font-light text-rose-800 mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
          >
            {couple_names || 'Nosso Casamento'}
          </motion.h1>
          
          <motion.div 
            className="flex flex-col md:flex-row items-center justify-center gap-6 text-rose-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            {formattedDate && (
              <div className="flex items-center gap-2 text-lg">
                <Calendar size={18} className="text-rose-400" />
                <span>{formattedDate}</span>
              </div>
            )}
            
            {wedding_location && formattedDate && (
              <div className="hidden md:block w-px h-6 bg-rose-300" />
            )}
            
            {wedding_location && (
              <div className="flex items-center gap-2 text-lg">
                <MapPin size={18} className="text-rose-400" />
                <span>{wedding_location}</span>
              </div>
            )}
          </motion.div>
          
          {/* Decorative line */}
          <motion.div 
            className="mt-12"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="flex items-center justify-center gap-4">
              <div className="w-20 h-px bg-gradient-to-r from-transparent to-rose-300" />
              <Heart className="text-rose-300" size={12} />
              <div className="w-20 h-px bg-gradient-to-l from-transparent to-rose-300" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Message Section */}
      {message && (
        <motion.section 
          className="relative py-24 md:py-32"
          {...fadeScale}
        >
          <div className="max-w-3xl mx-auto px-8 text-center relative z-10">
            <motion.div {...fadeUp}>
              <Heart className="mx-auto text-rose-300 fill-rose-100 mb-8" size={32} />
              <p className="text-2xl md:text-3xl text-rose-700 font-light leading-relaxed italic">
                "{message}"
              </p>
              <div className="mt-8 flex items-center justify-center gap-3">
                <div className="w-12 h-px bg-rose-200" />
                <Sparkles className="text-rose-300" size={16} />
                <div className="w-12 h-px bg-rose-200" />
              </div>
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Story Section */}
      {story && (
        <motion.section 
          className="relative py-24 md:py-32 bg-white/50"
          {...fadeScale}
        >
          <div className="max-w-3xl mx-auto px-8 relative z-10">
            <motion.div {...fadeUp} className="text-center mb-12">
              <span className="text-rose-400 tracking-[0.3em] uppercase text-xs">Uma história de amor</span>
              <h2 className="text-4xl md:text-5xl font-light text-rose-800 mt-4">
                Nossa História
              </h2>
              <div className="flex items-center justify-center gap-3 mt-6">
                <div className="w-16 h-px bg-gradient-to-r from-transparent to-rose-300" />
                <Heart className="text-rose-300" size={12} />
                <div className="w-16 h-px bg-gradient-to-l from-transparent to-rose-300" />
              </div>
            </motion.div>
            <motion.p 
              {...fadeUp}
              className="text-lg text-rose-700/80 leading-loose whitespace-pre-line text-center"
            >
              {story}
            </motion.p>
          </div>
        </motion.section>
      )}

      {/* Gallery Section */}
      {gallery_images.length > 0 && (
        <motion.section 
          className="relative py-24 md:py-32"
          {...fadeScale}
        >
          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <motion.div {...fadeUp} className="text-center mb-12">
              <span className="text-rose-400 tracking-[0.3em] uppercase text-xs">Nossos momentos</span>
              <h2 className="text-4xl md:text-5xl font-light text-rose-800 mt-4">
                Galeria
              </h2>
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
              {...staggerContainer}
            >
              {gallery_images.map((img, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg"
                >
                  <img 
                    src={img} 
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-rose-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Gift List Section */}
      {presentes && presentes.length > 0 && (
        <motion.section 
          className="relative py-24 md:py-32 bg-gradient-to-b from-rose-50 to-pink-100/50"
          {...fadeScale}
        >
          <div className="max-w-5xl mx-auto px-8 relative z-10">
            <motion.div {...fadeUp} className="text-center mb-16">
              <Gift className="mx-auto text-rose-400 mb-4" size={40} />
              <span className="text-rose-400 tracking-[0.3em] uppercase text-xs">Com carinho</span>
              <h2 className="text-4xl md:text-5xl font-light text-rose-800 mt-4">
                Lista de Presentes
              </h2>
              <p className="text-rose-500 mt-4 text-lg">
                Sua presença é nosso maior presente ♥
              </p>
            </motion.div>
            
            <motion.div 
              className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
              {...staggerContainer}
            >
              {presentes.map((presente, index) => (
                <motion.div
                  key={presente.id}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg border border-rose-100 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
                    <Gift className="text-rose-400" size={24} />
                  </div>
                  <h3 className="text-xl text-rose-800 mb-2">{presente.titulo}</h3>
                  {presente.descricao && (
                    <p className="text-sm text-rose-500 mb-4">{presente.descricao}</p>
                  )}
                  <p className="text-3xl text-rose-600 font-light mb-6">
                    {formatCurrency(presente.valor)}
                  </p>
                  <button
                    onClick={() => onPresentear(presente)}
                    className="w-full py-3 px-6 bg-gradient-to-r from-rose-400 to-pink-400 text-white rounded-full text-sm tracking-wider hover:from-rose-500 hover:to-pink-500 transition-all duration-300 shadow-md hover:shadow-lg"
                    data-testid={`gift-btn-${presente.id}`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Heart size={14} />
                      Presentear
                    </span>
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Footer */}
      <footer className="relative py-12 bg-rose-800 text-center">
        <p className="text-rose-200 text-sm flex items-center justify-center gap-2">
          Feito com <Heart className="text-rose-300 fill-rose-300" size={14} /> usando 
          <span className="font-semibold">liventy</span>
        </p>
      </footer>
    </div>
  );
}

export default RomanticTemplate;
